import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Grid,
  makeStyles,
  Theme,
  Typography,
  DialogActions,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import { DialogTitle } from 'components/Dialog';
import { BLACK, YELLOW } from 'constants/colors';
import { CloudDownloadRounded, WarningRounded } from '@material-ui/icons';
import NumberFormat from 'components/NumberFormat';
import { format } from 'date-fns';

interface Props {
  open: boolean;
  isLoading: boolean;
  handleClose: () => void;
  handleSubmit: () => void;
  handleReject: () => void;
  invoicePayment: PurchaseInvoicePaymentModel | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    borderRadius: '15px',
    padding: '7px 0'
  },

  titleContainer: {
    fontWeight: 500,
    fontSize: '1.2rem',
    color: BLACK,
    paddingLeft: '6px'
  },
  containerContent: {
    width: '100%',
    paddingTop: theme.spacing(2)
  },
  containerAction: {
    marginBottom: theme.spacing(1)
  },
  spacing: {
    marginTop: theme.spacing(2)
  },
  imageSection: {
    marginTop: theme.spacing(2)
  },
  containerDownloadAction: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '0.5em',
    marginBottom: '0.5em'
  },
  paymentImages: {
    objectFit: 'contain',
    width: '100%',
    height: 'auto',
    marginBottom: theme.spacing(1)
  },
  giroTable: {
    tableLayout: 'fixed',
    width: '100%'
  },
  giroHeaderCell: {
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: '6px 8px'
  },
  giroBodyCell: {
    fontSize: '0.9rem',
    padding: '6px 8px'
  },
  giroCellBank: { width: '25%' },
  giroCellNumber: { width: '15%' },
  giroCellAmount: { width: '25%' },
  giroCellDueDate: { width: '20%' },
  giroCellStatus: { width: '15%', textAlign: 'center' }
}));

const handleDownload = async (url: string, filename: string) => {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'include' });
    if (!res.ok) throw new Error('Network response was not ok');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download failed:', err);
    // fallback: buka tab baru
    window.open(url, '_blank');
  }
};

const Authorization = ({ invoicePayment, handleReject, open, handleClose, handleSubmit, isLoading }: Props) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={handleClose} scroll='paper' fullWidth maxWidth='sm'>
      <DialogTitle>
        <Grid item container direction='column' alignItems='center' xs={12}>
          <WarningRounded style={{ color: YELLOW, fontSize: '3rem' }} />
          <Typography className={classes.titleContainer}>Autorisasi Pembayaran Pembelian</Typography>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.containerContent}>
        <Grid xs={12}>
          <Typography align='center' style={{ fontSize: '1rem' }}>
            Apakah kamu yakin ingin autorisasi pembayaran pembelian ini?
          </Typography>

          {invoicePayment?.paymentMethod === 'Bank Transfer' && (
            <Grid item container direction='column' alignItems='center' style={{ marginTop: 16 }}>
              <Grid item>
                <Typography align='center' variant='h6'>
                  Tanggal Transfer
                </Typography>
                <Typography align='center' style={{ fontSize: '1rem' }}>
                  {invoicePayment?.transferDate
                    ? format(new Date(invoicePayment.transferDate as Date | string), 'dd MMM yyyy')
                    : invoicePayment?.payDate
                    ? format(new Date(invoicePayment.payDate as Date | string), 'dd MMM yyyy')
                    : '-'}
                </Typography>
              </Grid>
              <Grid item>
                <Typography align='center' variant='h6'>
                  Bank Transfer
                </Typography>
                <Typography align='center' style={{ fontSize: '1rem' }}>
                  {invoicePayment?.Bank?.bankAccount || '-'} - {invoicePayment?.Bank?.bankName || '-'} - {invoicePayment?.Bank?.bankNumber || '-'}
                </Typography>
              </Grid>
              <Grid item>
                <Typography align='center' variant='h6'>
                  Total Bayar
                </Typography>
                <Typography align='center' style={{ fontSize: '1rem' }}>
                  <NumberFormat value={invoicePayment.totalPay} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
        {invoicePayment?.paymentMethod === 'Giro' && (
          <Grid item container direction='column' style={{ marginTop: 16 }}>
            <Grid item>
              <Typography align='center' variant='h6'>
                Daftar Giro
              </Typography>
            </Grid>
            <Grid item style={{ width: '100%', marginTop: 8 }}>
              <Table size='small' className={classes.giroTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={`${classes.giroHeaderCell} ${classes.giroCellBank}`}>Bank</TableCell>
                    <TableCell className={`${classes.giroHeaderCell} ${classes.giroCellNumber}`}>Nomor</TableCell>
                    <TableCell className={`${classes.giroHeaderCell} ${classes.giroCellAmount}`} align='right'>
                      Jumlah
                    </TableCell>
                    <TableCell className={`${classes.giroHeaderCell} ${classes.giroCellDueDate}`}>Jatuh Tempo</TableCell>
                    <TableCell className={`${classes.giroHeaderCell} ${classes.giroCellStatus}`}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoicePayment?.giro ?? []).length > 0 ? (
                    (invoicePayment?.giro ?? []).map((g, idx) => (
                      <TableRow key={(g as any).id ?? idx}>
                        <TableCell className={`${classes.giroBodyCell} ${classes.giroCellBank}`}>{(g as any).giroBank ?? '-'}</TableCell>
                        <TableCell className={`${classes.giroBodyCell} ${classes.giroCellNumber}`}>{String((g as any).giroNumber ?? '-')}</TableCell>
                        <TableCell align='right' className={`${classes.giroBodyCell} ${classes.giroCellAmount}`}>
                          {(g as any).giroAmount != null ? (
                            <NumberFormat value={(g as any).giroAmount} prefix={'Rp '} thousandSeparator={true} displayType='text' />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className={`${classes.giroBodyCell} ${classes.giroCellDueDate}`}>
                          {(g as any).giroDueDate ? format(new Date((g as any).giroDueDate as Date | string), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell className={`${classes.giroBodyCell} ${classes.giroCellStatus}`}>{(g as any).status ?? '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        Tidak ada data giro
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        )}

        {invoicePayment?.PurchaseInvoicePaymentImages && invoicePayment.PurchaseInvoicePaymentImages.length > 0 && (
          <div className={classes.imageSection}>
            <Typography variant='h6' align='left' style={{ marginBottom: 8 }}>
              Bukti Transfer
            </Typography>
            {invoicePayment.PurchaseInvoicePaymentImages.map((val, key) => (
              <Grid container key={key} style={{ marginBottom: 16 }}>
                <Grid item xs={12}>
                  <img src={val.url} alt={`image payment ${key}`} loading='lazy' className={classes.paymentImages} />
                  <Typography variant='body2' className={classes.containerDownloadAction}>
                    <CloudDownloadRounded color='primary' />
                    <span
                      style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => handleDownload(val.url, 'Bukti Transfer.jpg')}
                    >
                      Download Gambar
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.containerAction}>
        <Grid item container direction='row' justify='space-evenly' alignItems='center'>
          <Grid xs={5}>
            <Button size='small' fullWidth className={classes.button} variant='outlined' onClick={handleReject}>
              Batal
            </Button>
          </Grid>

          <Grid xs={5}>
            <Button size='small' fullWidth disabled={isLoading} className={classes.button} color='primary' onClick={handleSubmit}>
              {isLoading ? <CircularProgress color='primary' size={20} /> : 'Autorisasi'}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default Authorization;
